import { Component, Property } from '@wonderlandengine/api';
import { vec3, quat } from 'gl-matrix';
import { VoxelBullet } from './bullet.js';
import { AudioSource } from '@wonderlandengine/components';

/**
 * fps-pistol
 *
 * Simple, extensible pistol component that uses pooled `VoxelBullet` instances
 * and instant hitscan raycasting for high-accuracy hit detection.
 */
export class FpsPistol extends Component {
    // ── Static Math Pool ──────────────────────────────────────────────────────
    static _vMuzzle   = vec3.create();
    static _vForward  = vec3.create();
    static _vTarget   = vec3.create();
    static _vDir      = vec3.create();
    static _vRight    = vec3.create();
    static _vUp       = vec3.create();
    static _worldUp   = vec3.fromValues(0, 1, 0);
    static _altUp     = vec3.fromValues(1, 0, 0);
    static _qRot      = quat.create();

    static TypeName = 'fps-pistol';
    static Properties = {
        bulletPrefab: Property.object(),

        // ── Desktop aim — make two empty child objects of the desktop camera:
        //    aimOriginDesktop  →  place at barrel root (or camera centre)
        //    aimTargetDesktop  →  place 1+ m further along the barrel axis
        // Direction = normalize(target - origin).  Both move with the camera
        // so aim is always correct after any mouse look rotation.
        aimOriginDesktop: Property.object(),
        aimTargetDesktop: Property.object(),

        // ── VR aim — make two empty child objects of the right-hand controller:
        //    aimOriginVR  →  place at controller barrel root
        //    aimTargetVR  →  place 1+ m further along the barrel axis
        // Both are children of the right hand so they rotate with the rig.
        aimOriginVR: Property.object(),
        aimTargetVR: Property.object(),

        // Legacy / fallback (kept for back-compat, ignored when aim objects are set)
        muzzle: Property.object(),
        vrMuzzleOrigin: Property.object(),
        vrMuzzleTarget: Property.object(),

        fireRate: Property.float(6.0),
        magazineSize: Property.int(12),
        reloadTime: Property.float(1.0),
        bulletSpeed: Property.float(600.0),
        spreadDeg: Property.float(0.2),
        poolSize: Property.int(32),
        automatic: Property.bool(false),
        ownerId: Property.string(''),
        damage: Property.int(20),
        physicsGroupMask: Property.int(1),
        maxHitDistance: Property.float(150.0),
    };

    init() {
        this._pool = [];        // local player visual tracers
        this._remotePool = [];  // remote players visual tracers (separate so they never compete)
        this._cooldown = 0.0;
        this._ammo = this.magazineSize;
        this._isReloading = false;
        this._isTriggerPulled = false;
    }

    start() {
        this.audio = this.object.getComponent(AudioSource);

        // Initialize visual tracer pool — local shots
        if (this.bulletPrefab) {
            for (let i = 0; i < this.poolSize; i++) {
                const obj = this.bulletPrefab.clone();
                const comp = obj.getComponent(VoxelBullet);
                // Ensure no PhysX component is present on the VoxelBullet instances,
                // as their collision is handled by VoxelTreadmill._isSolid.
                const physxComp = obj.getComponent('physx');
                if (physxComp) {
                    obj.removeComponent(physxComp);
                }
                const collisionComp = obj.getComponent('collision');
                if (collisionComp) {
                    obj.removeComponent(collisionComp);
                }
                /* Parent to treadmill so bullets don't rotate with the player rig */
                if (window.VoxelTreadmill?.object) {
                    obj.parent = window.VoxelTreadmill.object;
                }

                obj.setPositionWorld([0, -1000, 0]);
                obj.active = false;
                this._pool.push({ obj, comp });
            }
            // Remote pool — scene-root objects (no parent) so setPositionWorld
            // is pure scene-space with no treadmill parent-transform interference.
            const prefabMC = this.bulletPrefab.getComponent('mesh');
            for (let i = 0; i < this.poolSize; i++) {
                const obj = this.engine.scene.addObject(null); // scene root, no parent
                if (prefabMC) {
                    const mc = obj.addComponent('mesh');
                    mc.mesh     = prefabMC.mesh;
                    mc.material = prefabMC.material;
                }
                const comp = obj.addComponent(VoxelBullet);
                // Ensure no PhysX component is present on the VoxelBullet instances.
                const physxComp = obj.getComponent('physx');
                if (physxComp) {
                    obj.removeComponent(physxComp);
                }
                const collisionComp = obj.getComponent('collision');
                if (collisionComp) {
                    obj.removeComponent(collisionComp);
                }
                // Copy property values from prefab component
                const prefabComp = this.bulletPrefab.getComponent(VoxelBullet);
                if (prefabComp && comp) {
                    comp.maxLifetime       = prefabComp.maxLifetime;
                    comp.bulletSpeedScale  = prefabComp.bulletSpeedScale;
                    comp.damage            = prefabComp.damage;
                }
                obj.setPositionWorld([0, -1000, 0]);
                obj.active = false;
                this._remotePool.push({ obj, comp });
            }
        }

        // Register globally so multiplayer.js can spawn remote visual bullets
        window.FpsPistol = this;
    }

    update(dt) {
        if (window.GameState !== 'playing') return;
        if (this._cooldown > 0.0) {
            this._cooldown -= dt;
        }

        if (this.automatic && this._isTriggerPulled) {
            this._tryFireOnce();
        }
    }

    pullTrigger() {
        this._isTriggerPulled = true;
        if (!this.automatic) {
            this._tryFireOnce();
        }
    }

    releaseTrigger() {
        this._isTriggerPulled = false;
    }

    reload() {
        if (this._isReloading || this._ammo === this.magazineSize) return;
        this._isReloading = true;

        setTimeout(() => {
            this._ammo = this.magazineSize;
            this._isReloading = false;
            if (window.GameInterface && window.GameInterface.pushConsoleLog) {
                window.GameInterface.pushConsoleLog('RELOADED');
            }
        }, this.reloadTime * 1000);
    }

    _tryFireOnce() {
        if (this._isReloading) return false;
        if (this._ammo <= 0) { 
            this.reload(); 
            return false; 
        }
        if (this._cooldown > 0) return false;

        // Compute raycast origin (muzzle position) and shoot direction
        const muzzlePos = FpsPistol._vMuzzle;
        const forward   = FpsPistol._vForward;
        const dir       = FpsPistol._vDir;

        const isVR = this.engine.xr && this.engine.xr.session;

        let originNode = null;
        let targetNode = null;

        if (isVR) {
            originNode = this.aimOriginVR || this.vrMuzzleOrigin;
            targetNode = this.aimTargetVR || this.vrMuzzleTarget;
        } else {
            originNode = this.aimOriginDesktop || this.muzzle || this.object;
            targetNode = this.aimTargetDesktop;
        }

        if (originNode && targetNode) {
            originNode.getPositionWorld(muzzlePos);
            targetNode.getPositionWorld(FpsPistol._vTarget);
            
            // Calculate direction and speed (magnitude) using gl-matrix
            vec3.subtract(forward, FpsPistol._vTarget, muzzlePos);
            const calculatedSpeed = vec3.length(forward);
            if (calculatedSpeed > 0.0001) {
                vec3.scale(forward, forward, 1.0 / calculatedSpeed);
            }
        } else {
            // Fallback: Manually compute target point 1m in front to avoid getForward
            const rightHand = (isVR && window.VRControls) ? window.VRControls.rightInput : null;
            const cam = window.VoxelTreadmill?.camera;
            const src = originNode || rightHand || cam || this.object;

            src.getPositionWorld(muzzlePos);
            vec3.set(FpsPistol._vTarget, 0, 0, -1);
            src.transformPointWorld(FpsPistol._vTarget, FpsPistol._vTarget);
            vec3.subtract(forward, FpsPistol._vTarget, muzzlePos);
            vec3.normalize(forward, forward);
        }

        // Stable spread via orthonormal basis — correct in all look directions including
        // straight up/down where quat.rotationTo produces undefined roll.
        const spreadRad = (this.spreadDeg * Math.PI) / 180.0;
        const jX = (Math.random() * 2 - 1) * Math.tan(spreadRad);
        const jY = (Math.random() * 2 - 1) * Math.tan(spreadRad);
        const right  = FpsPistol._vRight;
        const upv    = FpsPistol._vUp;
        const worldUp = Math.abs(forward[1]) > 0.99 ? FpsPistol._altUp : FpsPistol._worldUp;
        vec3.cross(right, forward, worldUp);
        vec3.normalize(right, right);
        vec3.cross(upv, right, forward);
        vec3.normalize(upv, upv);
        dir[0] = forward[0] + right[0] * jX + upv[0] * jY;
        dir[1] = forward[1] + right[1] * jX + upv[1] * jY;
        dir[2] = forward[2] + right[2] * jX + upv[2] * jY;
        vec3.normalize(dir, dir);

        // ── HITSCAN ORIGIN ───────────────────────────────────────────────────
        const vt = window.VoxelTreadmill;
        const rayOrigin = (() => {
            if (!isVR) {
                const cam = vt?.camera;
                if (cam) return cam.getPositionWorld([]);
            }
            return muzzlePos; // VR: muzzlePos already comes from the controller
        })();

        // ── TERRAIN OCCLUSION CHECK ──────────────────────────────────────────
        const terrainHit = vt ? vt._raycastFrom(rayOrigin, dir, this.maxHitDistance) : null;
        const maxDist = terrainHit ? Math.min(this.maxHitDistance, terrainHit.dist) : this.maxHitDistance;

        // ─────────────────────────────────────────────────────────────────────────
        // PHYSICS RAYCAST — zombies + remote players (requires PhysX on both)
        // ─────────────────────────────────────────────────────────────────────────
        {
            const hit = this.engine.physics.rayCast(rayOrigin, dir, this.physicsGroupMask, maxDist);
            if (hit.hitCount > 0) {
                const hitObjects = hit.getObjects();
                outer: for (let i = 0; i < hit.hitCount; i++) {
                    const hitObj = hitObjects[i];

                    // Walk parent chain — PhysX collider may be on a child object
                    const zombies = window.ZombieBots;
                    if (zombies) {
                        for (const z of zombies) {
                            if (!z || !z._active || z._dead) continue;
                            let o = hitObj;
                            while (o) {
                                if (o === z.object) {
                                    const mp = window.OasisMultiplayer;
                                    if (!mp || mp.isHost || !mp._peer) {
                                        z.takeDamage(this.damage);
                                    } else {
                                        mp._broadcast?.({ type: 'zombie_hit', id: z.zombieId, damage: this.damage });
                                    }
                                    try { window.GameInterface?.pulseCrosshair?.(); } catch (_) {}
                                    break outer;
                                }
                                o = o.parent;
                            }
                        }
                    }

                    const remotePlayers = window.RemotePlayers;
                    if (remotePlayers) {
                        for (const [peerId, entry] of remotePlayers._active) {
                            let o = hitObj;
                            while (o) {
                                if (o === entry.obj) {
                                    window.OasisMultiplayer?.broadcastPlayerHit(peerId, this.damage);
                                    try { window.GameInterface?.pulseCrosshair?.(); } catch (_) {}
                                    break outer;
                                }
                                o = o.parent;
                            }
                        }
                    }
                }
            }
        }

        // ─────────────────────────────────────────────────────────────────────────
        // VISUAL TRACER EFFECTS (Pooled Visual Bullets)
        // ─────────────────────────────────────────────────────────────────────────
        // FIX: Calculate the accurate linear velocity vector (direction * speed)
        const linVel = vec3.create();
        vec3.scale(linVel, dir, this.bulletSpeed);

        const slot = this._getFreeSlot(this._pool);
        if (slot) {
            const targetObj = slot.obj || (slot.comp && slot.comp.object);
            if (targetObj) {
                targetObj.active = true;
            }

            // FIX: Pass 'linVel' instead of 'dir' so the bullet moves at actual speed
            if (slot.comp && typeof slot.comp.fireWithShooter === 'function') {
                slot.comp.fireWithShooter(muzzlePos, linVel, this.ownerId);
            } else if (slot.comp && typeof slot.comp.fire === 'function') {
                slot.comp.fire(muzzlePos, linVel);
                if (this.ownerId) slot.comp.shooterId = this.ownerId;
            }
        }

        this._ammo -= 1;
        this._cooldown = 1.0 / Math.max(0.0001, this.fireRate);

        // Broadcast visual bullet to peers
        try {
            let wox = muzzlePos[0], woy = muzzlePos[1], woz = muzzlePos[2];
            const _vt = window.VoxelTreadmill;
            if (_vt?.object) {
                const _vtBuf = new Float32Array(3);
                _vt.object.getPositionWorld(_vtBuf);
                wox = muzzlePos[0] - _vtBuf[0];
                woy = muzzlePos[1] - _vtBuf[1];
                woz = muzzlePos[2] - _vtBuf[2];
            }
            window.OasisMultiplayer?.broadcastBullet(wox, woy, woz, dir[0] * this.bulletSpeed, dir[1] * this.bulletSpeed, dir[2] * this.bulletSpeed);
        } catch (_) {}
        
        if (this.audio) {
            this.audio.play();
        }
        return true;
    }

    /**
     * Spawn a visual-only bullet from a remote player.
     * Reuses the local pool — no damage applied (VoxelBullet doesn’t deal damage).
     * @param {number} ox,oy,oz  world origin of the shot
     * @param {number} vx,vy,vz  velocity vector (dir × speed, before bulletSpeedScale)
     */
    spawnRemoteBullet(ox, oy, oz, vx, vy, vz) {
        const slot = this._getFreeSlot(this._remotePool);
        if (!slot) return;

        // FIX: Moved variable declarations up to function level scope to resolve ReferenceError
        let sx = ox, sy = oy, sz = oz;
        const vt = window.VoxelTreadmill;
        if (vt?.object) {
            const vtBuf = new Float32Array(3);
            vt.object.getPositionWorld(vtBuf);
            sx = ox + vtBuf[0];
            sy = oy + vtBuf[1];
            sz = oz + vtBuf[2];
        }

        const targetObj = slot.obj || (slot.comp && slot.comp.object);
        if (targetObj) {
            targetObj.active = true;          // activate BEFORE setting position
            targetObj.setPositionWorld([sx, sy, sz]);
        }

        // FIX: Pass the actual incoming velocity array [vx, vy, vz] instead of normalizing it to 1
        if (slot.comp) {
            slot.comp.fire([sx, sy, sz], [vx, vy, vz]);
        }
    }

    _getFreeSlot(pool) {
        for (let i = 0; i < pool.length; i++) {
            const { comp } = pool[i];
            if (comp && !comp._isActive) return pool[i];
        }
        return null;
    }
}